import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  Building2, MapPin, Droplets, Users, Activity, ArrowRight,
  StickyNote, Package, Wrench, Box,
} from 'lucide-react';
import { usePageHeader } from '../../stores/page-store';
import { useAuthStore } from '../../stores/auth-store';
import { dashboardApi } from '../../services/api-services';
import { navItems } from '../../config/navigation';
import { AUTH_STORAGE_KEY } from '../../config/api';
import type { SummaryDashboard } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// DashboardPage — "Clean Cards" design (design-mockup/Dashboard, Layout A):
//   Row 1 · System Online pill (right-aligned)
//   Row 2 · KPI cards: Companies · Locations · Wells · Users
//   Row 3 · Quick Actions (left) | Your Account (right)
// All counts come from real endpoints.
// ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const navigate = useNavigate();
  const role = user?.role ?? '';

  const [summary, setSummary] = useState<SummaryDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  // Dashboard has no page-level primary action (Add Well removed per request).
  usePageHeader('Dashboard', null, []);

  // KPI counts (cancellation guard: a re-run must not be overwritten by a stale response)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = isSuperAdmin
          ? await dashboardApi.getSummary()
          : await dashboardApi.getSummaryByCompany(Number(user?.companyId ?? 0));
        if (!cancelled && res.entity) setSummary(res.entity);
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [isSuperAdmin, user?.companyId]);

  // Last sign-in = the JWT's issued-at time.
  const lastSignIn = useMemo(() => {
    try {
      const tok = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!tok) return '—';
      const { iat } = jwtDecode<{ iat?: number }>(tok);
      if (!iat) return '—';
      const d = new Date(iat * 1000);
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return d.toDateString() === new Date().toDateString() ? `Today, ${time}` : `${d.toLocaleDateString()}, ${time}`;
    } catch { return '—'; }
  }, []);

  const allowed = (path: string) => {
    const item = navItems.find((n) => n.path === path);
    return !!item && !!role && item.roles.includes(role as never);
  };

  const kpis = [
    { title: 'Companies', count: summary?.noofCompanies, icon: Building2, sub: 'Registered companies', linkLabel: 'View companies', href: '/dashboard/companies' },
    { title: 'Surface Locations', count: summary?.noofSites, icon: MapPin, sub: 'Active surface locations', linkLabel: 'View surface locations', href: '/dashboard/sites' },
    { title: 'Wells', count: summary?.noofWells, icon: Droplets, sub: 'Total wells', linkLabel: 'View wells', href: '/dashboard/wells' },
    { title: 'Users', count: summary?.noofUsers, icon: Users, sub: 'Active accounts', linkLabel: 'View users', href: '/dashboard/users' },
  ].filter((k) => allowed(k.href));

  // Quick Actions — mockup order; only the actions the user's role can reach.
  const QUICK_ACTIONS = [
    { label: 'New Well', icon: Droplets, to: '/dashboard/wells/new', navPath: '/dashboard/wells', superAdminOnly: true },
    { label: 'New Annotation', icon: StickyNote, to: '/dashboard/annotations?new=1', navPath: '/dashboard/annotations' },
    { label: 'New Company', icon: Building2, to: '/dashboard/companies?new=1', navPath: '/dashboard/companies' },
    { label: 'New User', icon: Users, to: '/dashboard/users?new=1', navPath: '/dashboard/users' },
    { label: 'New Surface Location', icon: MapPin, to: '/dashboard/sites?new=1', navPath: '/dashboard/sites' },
    { label: 'New Tool', icon: Package, to: '/dashboard/inventory?new=1', navPath: '/dashboard/inventory' },
    { label: 'New Tool Model', icon: Wrench, to: '/dashboard/tool-models?new=1', navPath: '/dashboard/tool-models' },
    { label: 'Process 3D Model', icon: Box, to: '/dashboard/3d-models', navPath: '/dashboard/3d-models' },
  ].filter((a) => allowed(a.navPath) && (!a.superAdminOnly || isSuperAdmin));


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* System status pill */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SystemOnlinePill />
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {kpis.map((k) => (
          <KpiCard key={k.title} loading={loading} {...k} onOpen={() => navigate(k.href)} />
        ))}
      </div>

      {/* Quick Actions | Account */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <Panel title="Quick Actions" subtitle="Jump straight into a create flow">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {QUICK_ACTIONS.map((a) => (
              <QuickTile key={a.label} label={a.label} Icon={a.icon} onClick={() => navigate(a.to)} />
            ))}
          </div>
        </Panel>

        <Panel title="Your Account">
          <AccountRow label="Role" value={user?.role ?? '—'} />
          <AccountRow label="Company ID" value={user?.companyId && Number(user.companyId) > 0 ? String(user.companyId) : '—'} />
          <AccountRow label="Last sign-in" value={lastSignIn} last />
        </Panel>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function SystemOnlinePill() {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 13px', borderRadius: 20,
        background: '#e8f3e9', border: '1px solid #cfe6d2', color: '#2e7038',
        fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)', flexShrink: 0,
      }}
    >
      <Activity size={13} /> System Online
    </span>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '18px 20px' }}>
      <div style={{ marginBottom: subtitle ? 4 : 12 }}>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--color-text)', fontFamily: 'var(--font-body)' }}>{title}</span>
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginBottom: 14 }}>{subtitle}</div>
      )}
      {children}
    </div>
  );
}

function KpiCard({
  title, count, icon: Icon, sub, linkLabel, loading, onOpen,
}: {
  title: string; count: number | undefined; icon: React.ComponentType<{ size?: number }>; sub: string;
  linkLabel: string; loading: boolean; onOpen: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left', background: 'var(--color-panel)', borderRadius: 10, padding: '16px 18px 0',
        border: `1px solid ${hover ? 'var(--color-accent)' : 'var(--color-border)'}`,
        cursor: 'pointer', display: 'flex', flexDirection: 'column',
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 6px 18px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          style={{
            width: 40, height: 40, borderRadius: 10, background: 'var(--color-accent)', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Icon size={20} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: 'var(--color-text)', fontFamily: 'var(--font-display)' }}>
            {loading ? '…' : (count ?? 0).toLocaleString()}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{title}</span>
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 10 }}>{sub}</div>
      <div
        style={{
          marginTop: 12, padding: '10px 0', borderTop: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 5,
          fontSize: 12.5, fontWeight: 600, color: 'var(--color-accent)',
        }}
      >
        {linkLabel} <ArrowRight size={14} style={{ transition: 'transform 120ms', transform: hover ? 'translateX(3px)' : 'none' }} />
      </div>
    </button>
  );
}

function QuickTile({ label, Icon, onClick }: { label: string; Icon: React.ComponentType<{ size?: number }>; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10,
        padding: '15px 14px', borderRadius: 9, textAlign: 'left',
        background: 'var(--color-panel)',
        border: `1px solid ${hover ? 'var(--color-accent)' : 'var(--color-border)'}`,
        cursor: 'pointer', transition: 'border-color 120ms, transform 120ms, box-shadow 120ms',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 4px 12px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      <span
        style={{
          width: 32, height: 32, borderRadius: 8, background: 'rgba(87,154,59,0.10)', color: 'var(--color-accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon size={17} />
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)' }}>{label}</span>
    </button>
  );
}

function AccountRow({ label, value, valueColor, last }: { label: string; value: string; valueColor?: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '9px 0', borderBottom: last ? 'none' : '1px solid var(--color-border)',
      }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--color-text-dim)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: valueColor ?? 'var(--color-text)' }}>{value}</span>
    </div>
  );
}
